/**
 * Gate-area composite bits assembled ONLY from src/ui primitives + Views.
 * Segmented control, toggle switch, faux-QR placeholder, info note, icon tile —
 * all ported from design/source (styles.css .segmented/.seg, toggle buttons,
 * screens-services.jsx QR component).
 */
import React from 'react';
import { Pressable, View, type StyleProp, type ViewStyle } from 'react-native';

import QRCode from 'react-native-qrcode-svg';

import { palette, radius, space } from '@bero/tokens';
import { monthLong, translate } from '@/i18n';
import type { DictKey } from '@/i18n/en';
import { useTheme } from '@/theme';
import { AppText } from '@/ui';

import { Icon, type IconName } from './icons';

/* ---------------- Segmented control (.segmented / .seg) ---------------- */
export function Segmented({ options, labels, value, onChange }: {
  /** labels: optional display strings (i18n) aligned to options' indices. */
  options: string[]; labels?: string[]; value: string; onChange: (v: string) => void;
}) {
  const { colors } = useTheme();
  return (
    <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 6 }}>
      {options.map((opt, oi) => {
        const active = opt === value;
        return (
          <Pressable
            key={opt}
            accessibilityRole="button"
            accessibilityState={{ selected: active }}
            onPress={() => onChange(opt)}
            style={{
              minHeight: 44, paddingHorizontal: 14, paddingVertical: 9,
              borderRadius: radius.md, borderWidth: 1, justifyContent: 'center',
              borderColor: active ? colors.borderFirm : colors.border,
              backgroundColor: active ? colors.canvas2 : 'transparent',
            }}
          >
            <AppText
              variant="bodySm"
              weight="500"
              style={{ color: active ? colors.textPrimary : colors.textSecondary }}
            >
              {labels?.[oi] ?? opt}
            </AppText>
          </Pressable>
        );
      })}
    </View>
  );
}

/* ---------------- Toggle switch (design 46x28 pill) ---------------- */
export function Toggle({ on, onChange, label }: {
  on: boolean; onChange: (next: boolean) => void; label: string;
}) {
  const { colors } = useTheme();
  return (
    <Pressable
      accessibilityRole="switch"
      accessibilityLabel={label}
      accessibilityState={{ checked: on }}
      onPress={() => onChange(!on)}
      hitSlop={8}
      style={{
        width: 46, height: 28, borderRadius: radius.full, flexShrink: 0,
        backgroundColor: on ? colors.primary500 : colors.canvas2,
      }}
    >
      <View style={{
        position: 'absolute', top: 3, left: on ? 21 : 3,
        width: 22, height: 22, borderRadius: radius.full,
        backgroundColor: colors.canvas0,
      }} />
    </Pressable>
  );
}

/* ---------------- Faux QR (geometric placeholder, port of window.QR) ---------------- */
export function QRBox({ size = 168, seed = 'BERO-8429' }: { size?: number; seed?: string }) {
  const { colors } = useTheme();
  const inner = size - space.s2 * 2;
  // Scanners need dark-on-light: the QR patch stays light in BOTH themes
  // (an inverted QR frequently fails to scan), so colors come from the fixed
  // light palette, not the active theme.
  return (
    <View
      accessibilityLabel={`QR code ${seed}`}
      style={{
        width: size, height: size, padding: space.s2, borderRadius: radius.md,
        backgroundColor: palette.light.canvas0, borderWidth: 1, borderColor: colors.borderSoft,
      }}
    >
      <QRCode value={seed} size={inner} backgroundColor={palette.light.canvas0} color={palette.light.textPrimary} />
    </View>
  );
}

/* ---------------- Date grid (month calendar for "Pick a date" flows) ---------------- */
const WEEKDAY_KEYS: DictKey[] = ['wd_s0', 'wd_m', 'wd_t2', 'wd_w', 'wd_t4', 'wd_f', 'wd_s6'];

function isoOf(y: number, m: number, day: number): string {
  return `${y}-${String(m + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

/** Compact month-grid date picker — no free-text entry, no past dates.
 * `value`/`onChange` use plain YYYY-MM-DD (local, no time component). */
export function DateGrid({ value, onChange, minIso }: {
  value: string; onChange: (iso: string) => void; minIso?: string;
}) {
  const { colors } = useTheme();
  const today = new Date();
  const todayIso = isoOf(today.getFullYear(), today.getMonth(), today.getDate());
  const floor = minIso ?? todayIso;

  const initial = /^\d{4}-\d{2}-\d{2}$/.test(value) ? new Date(`${value}T00:00:00`) : today;
  const [cursor, setCursor] = React.useState({ y: initial.getFullYear(), m: initial.getMonth() });

  const firstOfMonth = new Date(cursor.y, cursor.m, 1);
  const daysInMonth = new Date(cursor.y, cursor.m + 1, 0).getDate();
  const leadingBlanks = firstOfMonth.getDay();
  const cells: (number | null)[] = [
    ...Array(leadingBlanks).fill(null),
    ...Array.from({ length: daysInMonth }, (_, i) => i + 1),
  ];
  const isCurrentMonth = cursor.y === today.getFullYear() && cursor.m === today.getMonth();

  const shift = (dir: -1 | 1) => {
    const d = new Date(cursor.y, cursor.m + dir, 1);
    setCursor({ y: d.getFullYear(), m: d.getMonth() });
  };

  return (
    <View style={{ borderWidth: 1, borderColor: colors.borderSoft, borderRadius: radius.lg, padding: space.s3 }}>
      <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: space.s2 }}>
        <Pressable accessibilityLabel="Previous month" onPress={() => shift(-1)} disabled={isCurrentMonth}
          style={{ width: 32, height: 32, alignItems: 'center', justifyContent: 'center' }}>
          <Icon name="chevron-left" size={18} color={isCurrentMonth ? colors.borderFirm : colors.textPrimary} />
        </Pressable>
        <AppText variant="bodyMd" weight="500">{monthLong(cursor.m)} {cursor.y}</AppText>
        <Pressable accessibilityLabel="Next month" onPress={() => shift(1)}
          style={{ width: 32, height: 32, alignItems: 'center', justifyContent: 'center' }}>
          <Icon name="chevron-right" size={18} color={colors.textPrimary} />
        </Pressable>
      </View>
      <View style={{ flexDirection: 'row' }}>
        {WEEKDAY_KEYS.map((wk, i) => (
          <View key={i} style={{ width: `${100 / 7}%`, alignItems: 'center', paddingVertical: 4 }}>
            <AppText variant="label" tone="tertiary">{translate(wk)}</AppText>
          </View>
        ))}
      </View>
      <View style={{ flexDirection: 'row', flexWrap: 'wrap' }}>
        {cells.map((day, i) => {
          if (day === null) return <View key={i} style={{ width: `${100 / 7}%`, aspectRatio: 1 }} />;
          const iso = isoOf(cursor.y, cursor.m, day);
          const disabled = iso < floor;
          const selected = iso === value;
          return (
            <View key={i} style={{ width: `${100 / 7}%`, aspectRatio: 1, alignItems: 'center', justifyContent: 'center' }}>
              <Pressable
                accessibilityRole="button"
                accessibilityState={{ selected, disabled }}
                disabled={disabled}
                onPress={() => onChange(iso)}
                style={{
                  width: 32, height: 32, borderRadius: radius.full,
                  alignItems: 'center', justifyContent: 'center',
                  backgroundColor: selected ? colors.primary500 : 'transparent',
                }}
              >
                <AppText
                  variant="bodySm"
                  weight={selected ? '600' : '400'}
                  style={{ color: disabled ? colors.borderFirm : selected ? colors.textOnDark : colors.textPrimary }}
                >
                  {day}
                </AppText>
              </Pressable>
            </View>
          );
        })}
      </View>
    </View>
  );
}

/* ---------------- Info note (.card-fill row with leading icon) ---------------- */
export function InfoNote({ icon, tint, children, style }: {
  icon: IconName; tint?: string; children: React.ReactNode; style?: StyleProp<ViewStyle>;
}) {
  const { colors } = useTheme();
  return (
    <View style={[{
      backgroundColor: colors.canvas1, borderRadius: radius.lg, padding: space.s3,
      flexDirection: 'row', gap: space.s2, alignItems: 'flex-start',
    }, style]}>
      <Icon name={icon} size={15} color={tint ?? colors.info500} style={{ marginTop: 2 }} />
      <AppText variant="bodySm" tone="secondary" style={{ flex: 1 }}>{children}</AppText>
    </View>
  );
}

/* ---------------- Icon tile (48px avatar-ic square) ---------------- */
export function IconTile({ name, size = 48, tint }: { name: IconName; size?: number; tint?: string }) {
  const { colors } = useTheme();
  return (
    <View style={{
      width: size, height: size, borderRadius: radius.md,
      backgroundColor: colors.canvas1, alignItems: 'center', justifyContent: 'center',
    }}>
      <Icon name={name} size={Math.round(size * 0.42)} color={tint ?? colors.textSecondary} />
    </View>
  );
}
